import { ReactElement, useState } from 'react';
import type { Dispatch, SetStateAction } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Checkbox,
  ExpandToggle,
  LinkButton,
  MultiSelect,
  RadioButton,
  SearchField,
  Spinner,
  Typography,
  VenueIcon,
} from '@weareopenr/component-library';
import { getBrandVenues } from 'src/api/getBrandVenues';
import { IVenue } from 'src/api/getBrandVenues/types';
import { IBrandVenueAccess, VenueAccessMode } from 'src/types';
import { QueryKeys } from 'src/constants/QueryKeys';
import { useUserTracking } from 'src/hooks';
import { venueAccessStatusLabel, venueCreateBlockedNote } from './venueAccessLabels';
import styles from './UserModal.module.scss';

const ACCESS_OPTIONS: { value: VenueAccessMode; label: string }[] = [
  { value: 'full', label: 'All venues' },
  { value: 'specific', label: 'Specific venues' },
  { value: 'none', label: 'No venue access' },
];

// Filter option id standing in for the API's null-id Uncategorised entry;
// real category ids are UUIDs so this cannot collide.
const UNCATEGORISED_FILTER_ID = 'uncategorised';

export interface IBrandAccessCardProps {
  brandId: string;
  brandName: string;
  isGranted: boolean;
  isWildcard: boolean;
  venueAccessEnabled: boolean;
  roleCanCreateVenues: boolean;
  entry?: IBrandVenueAccess;
  isExpanded: boolean;
  onToggleExpand: () => void;
  onToggleGrant: (checked: boolean) => void;
  onChange: (entry: IBrandVenueAccess) => void;
  setErrorMessage: Dispatch<SetStateAction<string | null>>;
}

export const BrandAccessCard = ({
  brandId,
  brandName,
  isGranted,
  isWildcard,
  venueAccessEnabled,
  roleCanCreateVenues,
  entry,
  isExpanded,
  onToggleExpand,
  onToggleGrant,
  onChange,
  setErrorMessage,
}: IBrandAccessCardProps): ReactElement => {
  const { captureException } = useUserTracking();
  const [venueSearch, setVenueSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string[]>([]);
  const isSpecific = entry?.access === 'specific';
  const hasChooser = isGranted && !isWildcard && venueAccessEnabled;
  const venueCreateNote = venueCreateBlockedNote(entry, roleCanCreateVenues);
  const venueCreateNoteId = `venue-create-note-${brandId}`;

  const { data: venuesData, isLoading } = useQuery({
    queryKey: [QueryKeys.BRAND_VENUES, brandId],
    queryFn: () => getBrandVenues(brandId),
    enabled: hasChooser && isSpecific,
    onError: (e: Error) => {
      setErrorMessage(e.message);
      captureException(e);
    },
  });

  const handleModeChange = (access: VenueAccessMode) => {
    if (access !== 'specific') {
      setVenueSearch('');
      setCategoryFilter([]);
    }
    onChange(
      access === 'specific'
        ? { brand_id: brandId, access, venue_ids: isSpecific ? entry?.venue_ids ?? [] : [] }
        : { brand_id: brandId, access },
    );
  };

  const handleToggleVenue = (venueId: string, checked: boolean) => {
    const current = entry?.venue_ids ?? [];
    const venueIds = checked ? [...current, venueId] : current.filter((id) => id !== venueId);
    onChange({ brand_id: brandId, access: 'specific', venue_ids: venueIds });
  };

  // The Checkbox's props type omits id, but its runtime forwards unknown
  // props to the input; a spread carries it through without a cast. The id
  // anchors the header's stretched label so bar clicks activate natively.
  const grantCheckboxId = { id: `brand-grant-${brandId}` };

  const renderVenueRow = (venue: IVenue): ReactElement => (
    <div key={venue.id} className={styles['venue-row']}>
      <Checkbox
        label={venue.name}
        checked={(entry?.venue_ids ?? []).includes(venue.id)}
        onChange={(event) => handleToggleVenue(venue.id, event.target.checked)}
      />
    </div>
  );

  const renderNoMatches = (): ReactElement => (
    <Typography size="small" className={styles['venue-empty']}>
      No matching venues
    </Typography>
  );

  const venues = venuesData?.data ?? [];
  const categories = venuesData?.categories ?? [];
  const query = venueSearch.trim().toLowerCase();

  const allowedIds =
    categoryFilter.length === 0
      ? null
      : new Set(
          categories
            .filter((category) => categoryFilter.includes(category.id ?? UNCATEGORISED_FILTER_ID))
            .flatMap((category) => category.venue_ids),
        );

  const visibleVenues = venues.filter(
    (venue) =>
      venue.name.toLowerCase().includes(query) && (allowedIds === null || allowedIds.has(venue.id)),
  );

  const allVisibleSelected =
    visibleVenues.length > 0 &&
    visibleVenues.every((venue) => (entry?.venue_ids ?? []).includes(venue.id));

  const handleToggleAllVisible = () => {
    const current = entry?.venue_ids ?? [];
    const visibleIds = visibleVenues.map((venue) => venue.id);
    const venueIds = allVisibleSelected
      ? current.filter((id) => !visibleIds.includes(id))
      : [...current, ...visibleIds.filter((id) => !current.includes(id))];
    onChange({ brand_id: brandId, access: 'specific', venue_ids: venueIds });
  };

  const renderVenueRows = (): ReactElement => {
    if (isLoading) {
      return (
        <div className={styles['venue-spinner']}>
          <Spinner size="small" />
        </div>
      );
    }

    if (venues.length === 0) {
      return (
        <Typography size="small" className={styles['venue-empty']}>
          This brand has no venues
        </Typography>
      );
    }

    if (visibleVenues.length === 0) {
      return renderNoMatches();
    }

    return (
      <fieldset aria-label={`${brandName} venues`} className={styles['venue-rows']}>
        {visibleVenues.map(renderVenueRow)}
      </fieldset>
    );
  };

  return (
    <section aria-label={`${brandName} access`} className={styles['brand-card']}>
      <div className={styles['brand-card-header']}>
        {/* Stretched native label: clicking anywhere on the bar activates the
            grant checkbox itself. Empty and aria-hidden, so the input's own
            aria-label keeps the accessible name; the chevron sits above it. */}
        <label
          htmlFor={`brand-grant-${brandId}`}
          aria-hidden="true"
          className={styles['brand-card-bar-target']}
        >
          <span className={styles['visually-hidden']}>{`Select ${brandName}`}</span>
        </label>
        <Checkbox
          {...grantCheckboxId}
          label={brandName}
          checked={isGranted || isWildcard}
          onChange={(event) => onToggleGrant(event.target.checked)}
        />
        {hasChooser && (
          <span className={styles['brand-card-status']}>{venueAccessStatusLabel(entry)}</span>
        )}
        {hasChooser && (
          <ExpandToggle
            isExpanded={isExpanded}
            className={styles['brand-card-toggle']}
            onClick={onToggleExpand}
          />
        )}
      </div>
      {hasChooser && isExpanded && (
        <div>
          <fieldset
            aria-label={brandName}
            aria-describedby={venueCreateNote ? venueCreateNoteId : undefined}
            className={styles['venue-access-modes']}
          >
            {ACCESS_OPTIONS.map((option) => (
              <RadioButton
                key={option.value}
                name={`venue-access-${brandId}`}
                value={option.value}
                checked={entry?.access === option.value}
                onChange={() => handleModeChange(option.value)}
              >
                {option.label}
              </RadioButton>
            ))}
          </fieldset>
          {venueCreateNote && (
            // Typography drops unknown props, so the id that anchors the
            // fieldset's aria-describedby lives on the wrapper.
            <div id={venueCreateNoteId} role="note" className={styles['venue-create-note']}>
              <Typography size="small">{venueCreateNote}</Typography>
            </div>
          )}
          {isSpecific && (
            <>
              <div className={styles['venue-filters']}>
                <SearchField
                  className={styles['venue-search']}
                  placeholder="Search venues"
                  isDisabled={isLoading}
                  onChange={(event) => setVenueSearch(event.target.value)}
                />
                {categories.length > 0 && (
                  <MultiSelect
                    className={styles['venue-category-filter']}
                    label="Venue categories"
                    name={`venue-categories-${brandId}`}
                    Icon={VenueIcon}
                    options={categories.map((category, index) => ({
                      id: category.id ?? UNCATEGORISED_FILTER_ID,
                      label: category.name,
                      display_order: index,
                    }))}
                    selected={categoryFilter}
                    onChange={setCategoryFilter}
                  />
                )}
                {!isLoading && visibleVenues.length > 0 && (
                  <LinkButton
                    type="button"
                    className={styles['venue-select-all']}
                    onClick={handleToggleAllVisible}
                  >
                    {allVisibleSelected ? 'Clear all' : `Select all (${visibleVenues.length})`}
                  </LinkButton>
                )}
              </div>
              {renderVenueRows()}
            </>
          )}
        </div>
      )}
    </section>
  );
};
